import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  Skeleton,
  Tooltip
} from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

type TrendDirection = 'up' | 'down' | 'flat' | 'none';
type TileVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';

interface StatTileProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  trend?: {
    value: number;
    direction: TrendDirection;
    label?: string;
  };
  variant?: TileVariant;
  loading?: boolean;
  onClick?: () => void;
  tooltip?: string;
}

const getVariantColors = (variant: TileVariant, isDark: boolean) => {
  const variants = {
    default: {
      bg: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
      border: isDark ? 'rgba(255, 255, 255, 0.1)' : '#e0e0e0',
      iconBg: isDark ? 'rgba(255, 255, 255, 0.1)' : '#f5f5f5',
      iconColor: isDark ? '#fff' : '#666'
    },
    primary: {
      bg: isDark ? '#1A2B4D' : '#E3F2FD',
      border: isDark ? '#4A90E2' : '#2196F3',
      iconBg: isDark ? 'rgba(74, 144, 226, 0.2)' : 'rgba(33, 150, 243, 0.15)',
      iconColor: '#2196F3'
    },
    success: {
      bg: isDark ? '#1A4D2B' : '#E8F5E9',
      border: isDark ? '#4AE290' : '#4CAF50',
      iconBg: isDark ? 'rgba(74, 226, 144, 0.2)' : 'rgba(76, 175, 80, 0.15)',
      iconColor: '#4CAF50'
    },
    warning: {
      bg: isDark ? '#4D2B1A' : '#FFF3E0',
      border: isDark ? '#E2904A' : '#FF9800',
      iconBg: isDark ? 'rgba(226, 144, 74, 0.2)' : 'rgba(255, 152, 0, 0.15)',
      iconColor: '#FF9800'
    },
    error: {
      bg: isDark ? '#4D1A1A' : '#FFEBEE',
      border: isDark ? '#E24A4A' : '#F44336',
      iconBg: isDark ? 'rgba(226, 74, 74, 0.2)' : 'rgba(244, 67, 54, 0.15)',
      iconColor: '#F44336'
    },
    info: {
      bg: isDark ? '#3A1A4D' : '#F3E5F5',
      border: isDark ? '#904AE2' : '#9C27B0',
      iconBg: isDark ? 'rgba(144, 74, 226, 0.2)' : 'rgba(156, 39, 176, 0.15)',
      iconColor: '#9C27B0'
    }
  };
  return variants[variant];
};

const StatTile: React.FC<StatTileProps> = ({
  title,
  value,
  subtitle,
  icon,
  trend,
  variant = 'default',
  loading = false,
  onClick,
  tooltip
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = getVariantColors(variant, isDark);

  const getTrendIcon = () => {
    if (!trend || trend.direction === 'none') return null;

    const iconSx = {
      fontSize: 16,
      color: trend.direction === 'up' ? '#4CAF50' : trend.direction === 'down' ? '#F44336' : '#9E9E9E'
    };

    switch (trend.direction) {
      case 'up':
        return <TrendingUp sx={iconSx} />;
      case 'down':
        return <TrendingDown sx={iconSx} />;
      case 'flat':
        return <TrendingFlat sx={iconSx} />;
      default:
        return null;
    }
  };

  const getTrendColor = () => {
    if (!trend) return theme.palette.text.secondary;
    if (trend.direction === 'up') return '#4CAF50';
    if (trend.direction === 'down') return '#F44336';
    return '#9E9E9E';
  };

  const cardContent = (
    <Card
      sx={{
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 2,
        transition: 'all 0.3s ease',
        cursor: onClick ? 'pointer' : 'default',
        height: '100%',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: isDark
            ? `0 8px 25px ${colors.border}40`
            : `0 8px 25px ${colors.border}30`
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {loading ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Skeleton variant="text" width={100} height={24} />
              <Skeleton variant="circular" width={44} height={44} />
            </Box>
            <Skeleton variant="text" width={80} height={48} />
            <Skeleton variant="text" width={120} height={20} />
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    mb: 1,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.75rem'
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 700,
                    fontSize: { xs: '1.75rem', sm: '2rem' },
                    lineHeight: 1.2
                  }}
                >
                  {value}
                </Typography>
              </Box>

              {icon && (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: colors.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${colors.border}30`,
                    '& svg': {
                      color: colors.iconColor,
                      fontSize: '1.5rem'
                    }
                  }}
                >
                  {icon}
                </Box>
              )}
            </Box>

            {/* Subtitle or Trend */}
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {trend && (
                <>
                  {getTrendIcon()}
                  <Typography
                    variant="body2"
                    sx={{
                      color: getTrendColor(),
                      fontWeight: 600,
                      fontSize: '0.8rem'
                    }}
                  >
                    {trend.value > 0 ? '+' : ''}{trend.value}%
                  </Typography>
                  {trend.label && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.8rem',
                        ml: 0.5
                      }}
                    >
                      {trend.label}
                    </Typography>
                  )}
                </>
              )}
              {subtitle && !trend && (
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: '0.85rem'
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} arrow>
        {cardContent}
      </Tooltip>
    );
  }

  return cardContent;
};

export default StatTile;
